import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
	ExclamationTriangleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import useModal from '../../hooks/useModal';
import { classNames } from '../helpers/functions';
import StackableModal from '../nextgen/StackableModal';

export const DangerModal = ({
	open,
	onClose,
	title,
	children,
	confirmText,
	onConfirm,
	cancelText,
	onCancel,
}) => {
	const cancelButtonRef = useRef(null);
	return (
		<StackableModal
			open={open}
			onClose={onClose}
			size="lg"
			showCloseIcon={false}
			initialFocusRef={cancelButtonRef}
		>
			<div className="sm:flex sm:items-start mb-5">
				<div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
					<ExclamationTriangleIcon
						className="h-6 w-6 text-red-600"
						aria-hidden="true"
					/>
				</div>
				<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
					<h3 className="text-lg font-medium leading-6 text-gray-900">
						{title}
					</h3>
					{children && (
						<div className="mt-2">
							<p className="text-sm text-gray-500">{children}</p>
						</div>
					)}
				</div>
			</div>
			<div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
				<button
					type="button"
					className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
					onClick={() => {
						onConfirm(false);
					}}
				>
					{confirmText}
				</button>
				<button
					type="button"
					className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
					onClick={() => (onCancel ? onCancel() : onClose(false))}
					ref={cancelButtonRef}
				>
					{cancelText || 'Cancel'}
				</button>
			</div>
		</StackableModal>
	);
};

export default function Modal({
	name,
	title,
	children,
	Footer,
	open,
	onClose,
	showCloseIcon = true,
	size = 'lg',
	zIndex = 50,
	noMargins = false,
	preventBackdropClose = false,
	dialogClassName,
	betaTitle = false,
}) {
	const { open: modalOpen, setClose } = useModal(name || '');

	const options = name ? modalOpen : open;
	const handleClose = name
		? showCloseIcon
			? onClose || setClose
			: () => {}
		: onClose;

	return (
		<Transition.Root show={!!options} as={Fragment}>
			<Dialog
				as="div"
				className="relative"
				style={{ zIndex }}
				onClose={() => (preventBackdropClose ? () => {} : handleClose())}
			>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div
						className="fixed inset-0 flex bg-gray-500/75 transition-opacity"
						style={{ zIndex }}
					/>
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto" style={{ zIndex }}>
					<div
						className={classNames(
							'flex min-h-full justify-center p-4 text-center items-center sm:p-0',
							size === 'full' && 'h-screen',
						)}
					>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<Dialog.Panel
								className={classNames(
									`relative transform  bg-white text-left shadow-xl transition-all w-full`,
									dialogClassName,
									size === 'full'
										? 'w-full h-screen'
										: noMargins
											? `max-w-${size} rounded-lg sm:w-full `
											: `max-w-${size} rounded-lg sm:w-full  px-4 pt-5  pb-4  sm:my-8 sm:p-6 `,
								)}
							>
								{showCloseIcon && (
									<div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
										<button
											type="button"
											className="rounded-md bg-white text-gray-400 hover:text-gray-500"
											onClick={() => handleClose()}
										>
											<span className="sr-only">Close</span>
											<XMarkIcon className="h-6 w-6" aria-hidden="true" />
										</button>
									</div>
								)}
								{title && (
									<>
										{betaTitle ? (
											<>
												<div className="sm:flex sm:items-start bg-red m-[-24px] p-[24px] border-radius border-b-gray">
													<div
														className={classNames(
															'mt-3 sm:mt-0 sm:mx-4 text-center sm:text-left flex-1',
														)}
													>
														<Dialog.Title
															as="h3"
															className="text-md font-normal leading-6 text-gray-700"
														>
															{title}
														</Dialog.Title>
													</div>
												</div>
											</>
										) : (
											<>
												<div className="sm:flex sm:items-start">
													<div
														className={classNames(
															noMargins ? '' : 'mt-3 sm:mt-0 sm:mx-4 ',
															'text-center sm:text-left flex-1',
														)}
													>
														<Dialog.Title
															as="h3"
															className="text-md font-normal leading-6 text-gray-700"
														>
															{title}
														</Dialog.Title>
													</div>
												</div>
											</>
										)}
									</>
								)}

								<div
									className={classNames(
										'text-center sm:text-left min-h-full',
										size === 'full'
											? 'h-full'
											: noMargins
												? ''
												: 'mt-3 sm:mt-4 sm:mx-4',
										dialogClassName,
									)}
								>
									{children}
								</div>

								{Footer && (
									<div className="mt-5 sm:mt-4 sm:mx-4 sm:flex sm:flex-row-reverse">
										{Footer}
									</div>
								)}
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
}
